import * as React from 'react';
import { PREFIXO_CRACHA, useSessao } from '@/hooks/useSessao';

/**
 * O crachá de quem está operando.
 *
 * A operação inteira do ERP é identificada por crachá: é ele que fecha
 * conferência, é ele que assina a NF-e, é ele que define de quem é a fila de
 * faturamento. O MegaFaturamento pede o número na entrada e ponto — sem senha,
 * sobre HTTP, qualquer um assume a fila de qualquer um.
 *
 * Aqui a ordem é outra: **o crachá vem da sessão**, porque o vínculo já existe
 * no cadastro do Procfit (`CRACHAS.USUARIO`) e quem já provou quem é no login
 * não deve provar de novo digitando um número.
 *
 * Trocar é TEMPORÁRIO e só desta sessão: vale nesta aba, para este usuário,
 * até sair ou fechar a aba. O WMS não grava nada em `dbo.CRACHAS` — o cadastro
 * é do Procfit, e foi o WMS mexendo nele que deixou 22 crachás sem usuário em
 * 01/10/2026. Sem vínculo no cadastro, a troca é o único caminho (digitar).
 */
export interface Cracha {
  /** O crachá em uso, ou `null` enquanto ninguém informou nada. */
  cracha: string | null;
  /** `true` quando é o do cadastro, sem troca — o caso bom. */
  daSessao: boolean;
  /** Se o usuário precisa digitar (não tem vínculo no cadastro nem troca). */
  precisaInformar: boolean;
  /** O crachá do cadastro, para voltar a ele depois de uma troca. */
  doCadastro: string | null;
  /** Impressora de NF-e do crachá do cadastro (a da troca o ERP resolve). */
  impressora: string | null;
  /** Troca por esta sessão; `null` volta para o do cadastro. */
  informar: (cracha: string | null) => void;
}

const EVENTO_TROCA = 'wms:cracha-trocado';

const ler = (chave: string | null): string | null => {
  if (!chave) return null;
  try {
    return sessionStorage.getItem(chave)?.trim() || null;
  } catch {
    return null;
  }
};

export function useCracha(): Cracha {
  const { usuario } = useSessao();
  const doCadastro = usuario?.cracha?.trim() || null;
  const chave = usuario ? `${PREFIXO_CRACHA}${usuario.usuario}` : null;

  const [trocado, definirTrocado] = React.useState<string | null>(() => ler(chave));
  React.useEffect(() => definirTrocado(ler(chave)), [chave]);

  // Cada componente tem a sua cópia do crachá; quem troca avisa os outros
  // desta aba (a impressão de etiquetas, no AppShell, segue a troca na hora).
  React.useEffect(() => {
    const aoTrocar = (): void => definirTrocado(ler(chave));
    window.addEventListener(EVENTO_TROCA, aoTrocar);
    return () => window.removeEventListener(EVENTO_TROCA, aoTrocar);
  }, [chave]);

  const informar = React.useCallback(
    (valor: string | null) => {
      const limpo = valor?.trim() || null;
      // Trocar para o próprio crachá do cadastro é desfazer a troca.
      const efetivo = limpo === doCadastro ? null : limpo;
      definirTrocado(efetivo);
      if (!chave) return;
      try {
        if (efetivo) sessionStorage.setItem(chave, efetivo);
        else sessionStorage.removeItem(chave);
      } catch {
        /* navegador com storage bloqueado: vale só enquanto a tela estiver aberta */
      }
      window.dispatchEvent(new Event(EVENTO_TROCA));
    },
    [chave, doCadastro],
  );

  const emUso = trocado ?? doCadastro;
  return {
    cracha: emUso,
    daSessao: trocado === null && doCadastro !== null,
    precisaInformar: emUso === null,
    doCadastro,
    impressora: trocado === null ? usuario?.impressoraNfe?.trim() || null : null,
    informar,
  };
}
